import { CopyOutlined, LinkOutlined, WarningOutlined } from "@ant-design/icons";
import {
  Button,
  Collapse,
  Descriptions,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import React from "react";
import styled from "styled-components";

import { AlertItem, FanoutResultItem, RunItem } from "./types";

const { Text } = Typography;

const DetailWrapper = styled.div`
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 16px 20px;
`;

const Section = styled.div`
  & + & {
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
  }
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #999;
  margin-bottom: 10px;
`;

const TimingGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const TimingChip = styled.div<{ $highlight?: boolean }>`
  background: ${({ $highlight }) => ($highlight ? "#e6f4ff" : "#f5f5f5")};
  border-radius: 4px;
  padding: 4px 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 80px;
`;

const TimingValue = styled.div<{ $highlight?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $highlight }) => ($highlight ? "#1677ff" : "#333")};
  font-variant-numeric: tabular-nums;
`;

const TimingLabel = styled.div`
  font-size: 10px;
  color: #aaa;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-top: 1px;
`;

const ReasoningText = styled.div`
  font-size: 13px;
  line-height: 1.6;
  color: #444;
  white-space: pre-wrap;
`;

const FindingsList = styled.ul`
  margin: 8px 0 0 0;
  padding-left: 20px;
  font-size: 13px;
  color: #555;
  line-height: 1.7;
`;

const ContentHeadline = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

const BulletList = styled.ul`
  margin: 0 0 8px 0;
  padding-left: 20px;
  font-size: 13px;
  color: #444;
  line-height: 1.7;
`;

const ContentReasoning = styled.div`
  font-size: 12px;
  color: #999;
  line-height: 1.6;
`;

const ImageThumb = styled.img`
  max-height: 90px;
  border-radius: 4px;
  border: 1px solid #e8e8e8;
  cursor: pointer;
  transition: box-shadow 0.15s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
`;

const ErrorPre = styled.pre`
  background: #fff1f0;
  border: 1px solid #ffa39e;
  border-radius: 4px;
  padding: 8px 12px;
  color: #cf1322;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  margin: 6px 0 0 0;
`;

const DeepLinkButton = styled(Button)`
  font-size: 12px;
`;

const PromptPre = styled.pre`
  margin: 8px 0 0;
  padding: 8px 10px;
  background: #f6f8fa;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  color: #333;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  max-height: 220px;
  overflow: auto;
`;

const SmallImage = styled.img`
  width: 48px;
  height: 32px;
  object-fit: cover;
  border-radius: 3px;
  border: 1px solid #e8e8e8;
`;

interface RunDetailPanelProps {
  run: RunItem;
  alert?: AlertItem;
}

function formatMs(ms?: number): string {
  if (ms === undefined || ms === null) {
    return "—";
  }
  if (ms >= 1000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  return `${ms}ms`;
}

function computeTotal(startedAt?: string, completedAt?: string): string {
  if (!startedAt || !completedAt) {
    return "—";
  }
  const diff = new Date(completedAt).getTime() - new Date(startedAt).getTime();
  if (diff < 0) {
    return "—";
  }
  if (diff >= 1000) {
    return `${(diff / 1000).toFixed(1)}s`;
  }
  return `${diff}ms`;
}

function formatDateTime(iso?: string): string {
  if (!iso) {
    return "—";
  }
  return new Date(iso).toLocaleString();
}

function sourceLabel(source?: string): string {
  if (source === "saved_preview") {
    return "Saved preview";
  }
  if (source === "last_successful_run") {
    return "Latest successful run";
  }
  return "None";
}

function sourceColor(source?: string): string {
  if (source === "saved_preview") {
    return "green";
  }
  if (source === "last_successful_run") {
    return "blue";
  }
  return "default";
}

function formatList(items?: string[]): string {
  if (!items || items.length === 0) {
    return "—";
  }
  return items.map(item => `- ${item}`).join("\n");
}

function buildDebugBundle(run: RunItem, alert?: AlertItem): string {
  return [
    "Smart Alert Debug Bundle",
    "",
    `User: ${alert?.email || "—"}`,
    `Alert: ${alert?.name || "Unnamed"} (${alert?.smart_alert_id || run.smart_alert_id})`,
    `Run: ${run.smart_alert_run_id}`,
    `Spreadsheet: ${alert?.spreadsheet_id || "—"}`,
    `Status: ${run.status || "—"} / ${run.trigger_result || "—"}`,
    "",
    "Config",
    `Trigger prompt:\n${alert?.trigger_prompt || "—"}`,
    `Content prompt:\n${alert?.content_prompt || "—"}`,
    `Presentation prompt:\n${alert?.presentation_prompt || "—"}`,
    `Granularity: ${alert?.granularity || "summary"}`,
    `Creativity: ${alert?.creativity_level || "—"}`,
    `Image orientation: ${alert?.image_orientation || "landscape"}`,
    "",
    "Trigger",
    `Reasoning:\n${run.trigger_reasoning || "—"}`,
    `Findings:\n${formatList(run.trigger_key_findings)}`,
    "",
    "Content",
    `Headline: ${run.content_headline || "—"}`,
    `Bullets:\n${formatList(run.content_bullet_points)}`,
    `Reasoning:\n${run.content_reasoning || "—"}`,
    "",
    "Image",
    `Style source: ${sourceLabel(alert?.style_reference_source)}`,
    `Generated image: ${run.generated_image_url || "—"}`,
    `Generated prompt:\n${run.generated_image_prompt || "—"}`,
    `Saved preview image: ${alert?.last_generated_image_url || "—"}`,
    `Saved preview prompt:\n${alert?.last_generated_image_prompt || "—"}`,
    `Latest successful run image: ${alert?.last_successful_run_image_url || "—"}`,
    `Latest successful run prompt:\n${alert?.last_successful_run_image_prompt || "—"}`,
    "",
    "Links",
    `LangSmith Trigger: ${run.langsmith_trigger_run_url || "—"}`,
    `LangSmith Content: ${run.langsmith_content_run_url || "—"}`,
    `Datadog: ${run.datadog_run_url || "—"}`,
  ].join("\n");
}

async function writeClipboardText(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.top = "-1000px";
  document.body.appendChild(textarea);
  textarea.select();
  const copied = document.execCommand("copy");
  document.body.removeChild(textarea);
  if (!copied) {
    throw new Error("Copy command failed");
  }
}

async function copyDebugBundle(run: RunItem, alert?: AlertItem) {
  try {
    await writeClipboardText(buildDebugBundle(run, alert));
    message.success("Debug bundle copied");
  } catch {
    message.error("Failed to copy debug bundle");
  }
}

function sortedFanoutResults(
  results?: FanoutResultItem[]
): Array<FanoutResultItem & { index: number }> {
  return (results || [])
    .map((item, index) => ({ ...item, index: index + 1 }))
    .sort((a, b) => {
      if (a.error && !b.error) {
        return -1;
      }
      if (!a.error && b.error) {
        return 1;
      }
      return a.index - b.index;
    });
}

export const RunDetailPanel: React.FC<RunDetailPanelProps> = ({ run, alert }) => {
  const hasDelivery =
    (run.emails_sent ?? 0) > 0 ||
    (run.slack_messages_sent ?? 0) > 0 ||
    (run.ms_teams_messages_sent ?? 0) > 0;
  const fanoutRows = sortedFanoutResults(run.fanout_results);

  return (
    <DetailWrapper>
      {/* Timing */}
      <Section>
        <SectionTitle>Timing</SectionTitle>
        <TimingGrid>
          <Tooltip title="Wall-clock time: completed_at − started_at">
            <TimingChip $highlight>
              <TimingValue $highlight>{computeTotal(run.started_at, run.completed_at)}</TimingValue>
              <TimingLabel>Total ⏱</TimingLabel>
            </TimingChip>
          </Tooltip>
          {(
            [
              {
                label: "Fetch",
                value: run.fetch_time_ms,
                tooltip: "Time to fetch spreadsheet data",
              },
              { label: "Diff", value: run.diff_time_ms, tooltip: "Time to compute data diff" },
              {
                label: "Trigger",
                value: run.trigger_evaluation_time_ms,
                tooltip: "LLM trigger agent evaluation time",
              },
              {
                label: "Content",
                value: run.content_generation_time_ms,
                tooltip:
                  (run.fanout_count ?? 0) > 1
                    ? `LLM content agent time. In fanout mode this is for the single agent call that produces all ${run.fanout_count} items.`
                    : "LLM content agent time",
              },
              {
                label: "Image",
                value: run.image_generation_time_ms,
                tooltip:
                  (run.fanout_count ?? 0) > 1
                    ? `Cumulative image generation time summed across all ${run.fanout_count} fanout items (generated in parallel). May exceed Total wall-clock time.`
                    : "Image generation time",
              },
            ] as Array<{ label: string; value?: number; tooltip: string }>
          ).map(({ label, value, tooltip }) => (
            <Tooltip key={label} title={tooltip}>
              <TimingChip>
                <TimingValue>{formatMs(value)}</TimingValue>
                <TimingLabel>{label}</TimingLabel>
              </TimingChip>
            </Tooltip>
          ))}
        </TimingGrid>
        <div style={{ marginTop: 10 }}>
          <Descriptions size="small" column={2} colon>
            <Descriptions.Item label="Started">{formatDateTime(run.started_at)}</Descriptions.Item>
            <Descriptions.Item label="Completed">
              {formatDateTime(run.completed_at)}
            </Descriptions.Item>
          </Descriptions>
        </div>
      </Section>

      {/* Trigger reasoning */}
      {(run.trigger_reasoning || (run.trigger_key_findings?.length ?? 0) > 0) && (
        <Section>
          <SectionTitle>Trigger Reasoning</SectionTitle>
          <Collapse ghost defaultActiveKey={[]}>
            <Collapse.Panel
              key="reasoning"
              header={
                <Text style={{ fontSize: 13 }}>
                  {run.trigger_reasoning
                    ? run.trigger_reasoning.slice(0, 120) +
                      (run.trigger_reasoning.length > 120 ? "…" : "")
                    : "View reasoning"}
                </Text>
              }
            >
              {run.trigger_reasoning && <ReasoningText>{run.trigger_reasoning}</ReasoningText>}
              {(run.trigger_key_findings?.length ?? 0) > 0 && (
                <FindingsList>
                  {run.trigger_key_findings!.map((f, i) => (
                    <li key={i}>{f}</li>
                  ))}
                </FindingsList>
              )}
            </Collapse.Panel>
          </Collapse>
        </Section>
      )}

      {/* Content — only when fired */}
      {run.trigger_result === "fired" && (run.content_headline || run.generated_image_url) && (
        <Section>
          <SectionTitle>Generated Content</SectionTitle>
          <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
            {run.generated_image_url && (
              <a href={run.generated_image_url} target="_blank" rel="noopener noreferrer">
                <Tooltip title="Click to open full image">
                  <ImageThumb src={run.generated_image_url} alt="Generated" />
                </Tooltip>
              </a>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              {run.content_headline && <ContentHeadline>{run.content_headline}</ContentHeadline>}
              {(run.content_bullet_points?.length ?? 0) > 0 && (
                <BulletList>
                  {run.content_bullet_points!.map((pt, i) => (
                    <li key={i}>{pt}</li>
                  ))}
                </BulletList>
              )}
              {run.content_reasoning && (
                <ContentReasoning>{run.content_reasoning}</ContentReasoning>
              )}
              {run.generated_image_prompt && (
                <Collapse ghost defaultActiveKey={[]}>
                  <Collapse.Panel header="Generated image prompt" key="image-prompt">
                    <PromptPre>{run.generated_image_prompt}</PromptPre>
                  </Collapse.Panel>
                </Collapse>
              )}
            </div>
          </div>
        </Section>
      )}

      {alert && (
        <Section>
          <SectionTitle>Style Reference</SectionTitle>
          <Descriptions size="small" column={1} bordered>
            <Descriptions.Item label="Real-run source">
              <Tag color={sourceColor(alert.style_reference_source)}>
                {sourceLabel(alert.style_reference_source)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Saved preview image">
              {alert.last_generated_image_url ? (
                <Text copyable={{ text: alert.last_generated_image_url }}>
                  {alert.last_generated_image_url}
                </Text>
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Saved preview prompt">
              {alert.last_generated_image_prompt ? (
                <PromptPre>{alert.last_generated_image_prompt}</PromptPre>
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Latest successful run image">
              {alert.last_successful_run_image_url ? (
                <Text copyable={{ text: alert.last_successful_run_image_url }}>
                  {alert.last_successful_run_image_url}
                </Text>
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Latest successful run prompt">
              {alert.last_successful_run_image_prompt ? (
                <PromptPre>{alert.last_successful_run_image_prompt}</PromptPre>
              ) : (
                <Text type="secondary">—</Text>
              )}
            </Descriptions.Item>
          </Descriptions>
        </Section>
      )}

      {fanoutRows.length > 0 && (
        <Section>
          <SectionTitle>Fanout Items</SectionTitle>
          <Table
            dataSource={fanoutRows}
            rowKey="index"
            size="small"
            pagination={{ pageSize: 10, hideOnSinglePage: true, showSizeChanger: false }}
            columns={[
              {
                title: "#",
                dataIndex: "index",
                key: "index",
                width: 50,
              },
              {
                title: "Image",
                dataIndex: "image_url",
                key: "image_url",
                width: 70,
                render: (url?: string) =>
                  url ? (
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <SmallImage src={url} alt="Fanout item" />
                    </a>
                  ) : (
                    <Text type="secondary">—</Text>
                  ),
              },
              {
                title: "Headline",
                dataIndex: "headline",
                key: "headline",
                render: (headline?: string) => (
                  <Tooltip title={headline}>
                    <Text style={{ fontSize: 12 }}>
                      {headline && headline.length > 80
                        ? `${headline.slice(0, 80)}…`
                        : headline || "—"}
                    </Text>
                  </Tooltip>
                ),
              },
              {
                title: "Delivery",
                key: "delivery",
                width: 180,
                render: (_: unknown, item: FanoutResultItem) => (
                  <Space size={4} wrap>
                    <Tag color={(item.emails_sent ?? 0) > 0 ? "green" : "default"}>
                      Email {item.emails_sent ?? 0}
                    </Tag>
                    <Tag color={(item.slack_sent ?? 0) > 0 ? "green" : "default"}>
                      Slack {item.slack_sent ?? 0}
                    </Tag>
                    <Tag color={(item.ms_teams_sent ?? 0) > 0 ? "green" : "default"}>
                      Teams {item.ms_teams_sent ?? 0}
                    </Tag>
                  </Space>
                ),
              },
              {
                title: "Error",
                dataIndex: "error",
                key: "error",
                width: 220,
                render: (error?: string) =>
                  error ? (
                    <Tooltip title={error}>
                      <Tag color="red">{error.length > 40 ? `${error.slice(0, 40)}…` : error}</Tag>
                    </Tooltip>
                  ) : (
                    <Tag color="green">OK</Tag>
                  ),
              },
            ]}
          />
        </Section>
      )}

      {/* Delivery */}
      {(run.trigger_result === "fired" || hasDelivery || run.delivery_error) && (
        <Section>
          <SectionTitle>Delivery</SectionTitle>
          <Space size="middle">
            <Text style={{ fontSize: 13 }}>
              📧 <strong>{run.emails_sent ?? 0}</strong> email
            </Text>
            <Text style={{ fontSize: 13 }}>
              💬 <strong>{run.slack_messages_sent ?? 0}</strong> Slack
            </Text>
            <Text style={{ fontSize: 13 }}>
              🔷 <strong>{run.ms_teams_messages_sent ?? 0}</strong> Teams
            </Text>
            {run.fanout_count !== undefined && run.fanout_count > 0 && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                ({run.fanout_count} fanout items)
              </Text>
            )}
          </Space>
          {run.delivery_error && (
            <div style={{ marginTop: 8 }}>
              <Text style={{ color: "#cf1322", fontSize: 12 }}>{run.delivery_error}</Text>
            </div>
          )}
        </Section>
      )}

      {/* Error — only when FAILED */}
      {run.status === "FAILED" && (run.error_type || run.error_message) && (
        <Section>
          <SectionTitle>
            <WarningOutlined style={{ color: "#cf1322", marginRight: 4 }} />
            Error
          </SectionTitle>
          {run.error_type && (
            <Tag color="red" style={{ marginBottom: 6 }}>
              {run.error_type}
            </Tag>
          )}
          {run.error_message && <ErrorPre>{run.error_message}</ErrorPre>}
        </Section>
      )}

      {/* Deep Links */}
      <Section>
        <SectionTitle>Debug Links</SectionTitle>
        <Space wrap>
          <Button size="small" icon={<CopyOutlined />} onClick={() => copyDebugBundle(run, alert)}>
            Copy Debug Bundle
          </Button>
          {run.langsmith_trigger_run_url && (
            <DeepLinkButton
              size="small"
              icon={<LinkOutlined />}
              href={run.langsmith_trigger_run_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              LangSmith — Trigger
            </DeepLinkButton>
          )}
          {run.langsmith_content_run_url && (
            <DeepLinkButton
              size="small"
              icon={<LinkOutlined />}
              href={run.langsmith_content_run_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              LangSmith — Content
            </DeepLinkButton>
          )}
          {run.datadog_run_url && (
            <DeepLinkButton
              size="small"
              icon={<LinkOutlined />}
              href={run.datadog_run_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Datadog Logs
            </DeepLinkButton>
          )}
          {!run.langsmith_trigger_run_url &&
            !run.langsmith_content_run_url &&
            !run.datadog_run_url && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                No debug links available for this run
              </Text>
            )}
        </Space>
      </Section>
    </DetailWrapper>
  );
};
